import { Routes } from '@angular/router';

import { MinAccessLevelGuard } from '../core/guards/min-access-level.guard';
import { UserAccessLevel } from '../core/services/user.service';
import { AppFeatures } from '../core/app.features';

import { FeedResolver } from '../feed/feed.resolver';
import { UserResolver } from '../admin/user.resolver';
import { DashboardResolver } from '../dashboard/dashboard.resolver';
import { WorkspaceResolver } from '../workspace/workspace.resolver';
import { AuthentificationResolver } from '../core/authetification.resolver';

import { ContextualNotFoundComponent } from '../shared/components/contextual-not-found/contextual-not-found.component';

export const SHELL_ROUTES: Routes = [
  {
    path: '',
    resolve: {
      user: AuthentificationResolver,
    },
    data: {
      section: 'analytics',
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'analytics',
      },
      {
        path: 'analytics',
        // Authentication for the whole analytics tree (the former per-child
        // canLoad entries never ran: canLoad applies to lazy modules only).
        // Sections hidden from the menu re-assert their level or feature below.
        canActivate: [MinAccessLevelGuard],
        loadComponent: () =>
          import('./pages/analytics/analytics.component').then(
            (m) => m.AnalyticsComponent,
          ),
        data: {
          minAccessLevel: UserAccessLevel.GUEST,
          breadcrumb: 'breadcrumb.anlytics.analytics',
          breadcrumbIcon: ['fas', 'chart-line'],
          pageTitle: 'page-title.anlytics.analytics',
        },
        children: [
          {
            path: '',
            redirectTo: 'home',
            pathMatch: 'full',
          },
          {
            path: 'home',
            loadComponent: () =>
              import('../home/pages/home/home.component').then(
                (m) => m.HomeComponent,
              ),
            data: {
              // breadcrumb: 'breadcrumb.anlytics.home',
              // breadcrumbIcon: ['fas', 'home'],
            },
          },
          {
            path: 'user/:userId',
            loadComponent: () =>
              import('../home/pages/user-profile/user-profile.component').then(
                (m) => m.UserProfileComponent,
              ),
            resolve: {
              user: UserResolver,
            },
          },
          {
            path: 'connectors',
            canActivate: [MinAccessLevelGuard],
            loadChildren: () =>
              import('../connector-manager/connector-manager.routes').then(
                (m) => m.CONNECTOR_MANAGER_ROUTES,
              ),
            data: {
              minAccessLevel: UserAccessLevel.CLIENT_ADMIN,
              breadcrumb: 'breadcrumb.analytics.connectors',
              breadcrumbIcon: ['fas', 'plug'],
              breadcrumbContext: 'connectors',
              pageTitle: 'page-title.analytics.connectors',
              // Company-scoped mount; the Admin module mounts the same lazy
              // module cross-company (WAP-1629, connector-manager.scope.ts).
              connectorManagerScope: 'analytics',
            },
          },
          {
            path: 'feeds',
            loadComponent: () =>
              import('../feed/pages/feeds/feeds.component').then(
                (m) => m.FeedsComponent,
              ),

            data: {
              breadcrumb: 'breadcrumb.analytics.feeds',
              breadcrumbIcon: ['fas', 'rss'],
              breadcrumbContext: 'feeds',
              pageTitle: 'page-title.analytics.feeds',
            },
          },
          {
            path: 'dimensions',
            loadComponent: () =>
              import('../dimensions/pages/dimensions/dimensions.component').then(
                (m) => m.DimensionsComponent,
              ),

            data: {
              breadcrumb: 'breadcrumb.analytics.dimensions',
              breadcrumbIcon: ['fas', 'rss'],
              breadcrumbContext: 'dimensions',
              pageTitle: 'page-title.analytics.dimensions',
            },
          },
          {
            path: 'dashboards',
            loadComponent: () =>
              import('../dashboard/pages/dashboards/dashboards.component').then(
                (m) => m.DashboardsComponent,
              ),

            data: {
              breadcrumbContext: 'dashboards',
              breadcrumb: 'breadcrumb.analytics.dashboards',
              breadcrumbIcon: ['fas', 'tachometer-alt'],
              pageTitle: 'page-title.analytics.dashboards',
            },
          },
          {
            path: 'alerts',
            loadComponent: () =>
              import('../alerts/pages/alerts/alerts.component').then(
                (m) => m.AlertsComponent,
              ),

            data: {
              breadcrumbContext: 'alerts',
              breadcrumb: 'breadcrumb.analytics.alerts',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.alerts',
            },
          },
          {
            path: 'home/:tab',
            loadComponent: () =>
              import('../home/pages/home/home.component').then(
                (m) => m.HomeComponent,
              ),
            data: {},
          },
          {
            path: 'schedules',
            loadComponent: () =>
              import('../export-schedules/pages/export-schedules-grid/export-schedules-grid.component').then(
                (m) => m.ExportSchedulesGridComponent,
              ),
            data: {
              schedulesScope: 'my',
              breadcrumbContext: 'schedules',
              breadcrumb: 'breadcrumb.analytics.schedules',
              breadcrumbIcon: ['fas', 'calendar-days'],
              pageTitle: 'page-title.analytics.schedules',
            },
          },
          {
            path: 'auto-story-runs',
            canActivate: [MinAccessLevelGuard],
            loadComponent: () =>
              import('../auto-story-runs/pages/auto-story-runs/auto-story-runs.component').then(
                (m) => m.AutoStoryRunsComponent,
              ),
            data: {
              minAccessLevel: UserAccessLevel.CLIENT_USER,
              requiredFeature:
                AppFeatures.ADVANCED_MASS_OPERATIONS_AI_AUTO_STORY,
              breadcrumbContext: 'auto-story-runs',
              breadcrumb: 'breadcrumb.analytics.auto-story-runs',
              breadcrumbIcon: ['fas', 'wand-magic-sparkles'],
              pageTitle: 'page-title.analytics.auto-story-runs',
            },
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('../notifications/pages/notifications/notifications.component').then(
                (m) => m.NotificationsComponent,
              ),
            data: {
              breadcrumb: 'breadcrumb.notifications.my-notifications',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.my-notifications',
            },
          },
          {
            path: 'alerts-center',
            loadComponent: () =>
              import('../alerts/pages/alerts-center/alerts-center.component').then(
                (m) => m.AlertsCenterComponent,
              ),
            data: {
              breadcrumb: 'breadcrumb.notifications.my-alerts',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.my-alerts',
            },
          },
          {
            path: 'workspace/:workspaceId',
            loadComponent: () =>
              import('../workspace/pages/workspace/workspace.component').then(
                (m) => m.WorkspaceComponent,
              ),
            data: { breadcrumbContext: 'workspace' },
            resolve: {
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/:tab',
            loadComponent: () =>
              import('../workspace/pages/workspace/workspace.component').then(
                (m) => m.WorkspaceComponent,
              ),
            data: { breadcrumbContext: 'workspace' },

            resolve: {
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/feed/:feedId',
            loadComponent: () =>
              import('../feed/pages/feed/feed.component').then(
                (m) => m.FeedComponent,
              ),
            data: {},
            resolve: {
              feed: FeedResolver,
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/dashboard/:dashboardId',
            loadComponent: () =>
              import('../dashboard/pages/dashboard/dashboard.component').then(
                (m) => m.DashboardComponent,
              ),
            data: {},
            resolve: {
              workspace: WorkspaceResolver,
              dashboard: DashboardResolver,
            },
            children: [
              {
                path: 'page/:pageId',
                loadComponent: () =>
                  import('../dashboard/pages/dashboard-page/dashboard-page.component').then(
                    (m) => m.DashboardPageComponent,
                  ),
              },
              {
                path: '',
                loadComponent: () =>
                  import('../dashboard/pages/dashboard-page/dashboard-page.component').then(
                    (m) => m.DashboardPageComponent,
                  ),
              },
            ],
          },
          {
            path: 'workspace/:workspaceId/dashboard/:dashboardId/feed/:feedId',
            loadComponent: () =>
              import('../feed/pages/feed/feed.component').then(
                (m) => m.FeedComponent,
              ),
            data: {},
            resolve: {
              workspace: WorkspaceResolver,
              dashboard: DashboardResolver,
              feed: FeedResolver,
            },
          },

          { path: '**', component: ContextualNotFoundComponent },
        ],
      },
    ],
  },
];
